import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faChevronLeft, 
  faChevronRight, 
  faCheckCircle, 
  faCheck, 
  faPlayCircle, 
  faFileAlt, 
  faCodeBranch,
  faCalendar,
  faUser,
  faGlobe,
  faCode,
  faArrowLeft,
  faBars
} from '@fortawesome/free-solid-svg-icons'
import { faGithub } from '@fortawesome/free-brands-svg-icons'
import Topnavbar from '../Header-pages/Topnavbar'
import Navbar from '../Header-pages/Navbar'
import Footer from '../Header-pages/Footer'
import { API_URL } from '../utils/Function'
import { Helmet } from "react-helmet-async";

export default function ProjectDetailspage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [activeTab, setActiveTab] = useState('overview')

  // Fetch project details by slug
  const fetchProjectDetails = useCallback(async () => {
    try {
      setLoading(true);
      // First try to get by slug
      let response = await fetch(`${API_URL}/project.php/get`);
      let data = await response.json();
      
      if (data.success) {
        // Find project by slug
        const foundProject = data.projects.find(p => p.slug === slug);
        
        if (foundProject) {
          setProject(foundProject);
        } else {
          // If not found by slug, try to find by ID (fallback)
          const projectById = data.projects.find(p => p.id.toString() === slug);
          if (projectById) {
            setProject(projectById);
          } else {
            setError('Project not found');
          }
        }
      } else {
        setError(data.error || 'Failed to fetch projects');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) {
      fetchProjectDetails();
    }
  }, [slug, fetchProjectDetails]);

  // Auto slide functionality
  useEffect(() => {
    if (!project?.gallery_images_urls?.length) return

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => 
        prev === project.gallery_images_urls.length - 1 ? 0 : prev + 1
      )
    }, 5000)

    return () => clearInterval(interval)
  }, [project?.gallery_images_urls])

  const nextImage = () => {
    if (project?.gallery_images_urls) {
      setCurrentImageIndex(prev => 
        prev === project.gallery_images_urls.length - 1 ? 0 : prev + 1
      )
    }
  }

  const prevImage = () => {
    if (project?.gallery_images_urls) {
      setCurrentImageIndex(prev => 
        prev === 0 ? project.gallery_images_urls.length - 1 : prev - 1
      )
    }
  }

  const goToImage = (index) => {
    setCurrentImageIndex(index)
  }


  if (loading) {
    return (
      <>
        <Topnavbar />
        <Navbar />
        <div className="container text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading project details...</p>
        </div>
        <Footer />
      </>
    )
  }

  const images = project.gallery_images_urls || []
  const hasGallery = images.length > 0
  const mainImage = hasGallery ? images[currentImageIndex] : project.featured_image_url

  // Safely parse arrays with fallbacks
  const technologiesArray = project.technologies_array || 
                           (project.technologies ? JSON.parse(project.technologies) : []) || 
                           []

  const featuresArray = project.features_array || 
                       (project.features ? JSON.parse(project.features) : []) || 
                       []

  return (
    <>
    <Helmet>
        <title>
          {project?.short_description
            ? project.short_description
            : `${project?.short_description} | SAFPRO Technology Solutions`}
        </title>

        <meta 
          name="description" 
          content={project?.meta_description || project?.short_description || project?.title} 
        />
      </Helmet>
      <Topnavbar />
      <Navbar />
      
      <div className="min-vh-100 bg-light">
        {/* Hero Section with Background Image */}
        <div 
          className="position-relative bg-dark" 
          style={{
            height: '300px',
            backgroundImage: 'url("/images/Productview.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <div 
            className="position-absolute top-0 start-0 end-0 bottom-0 d-flex flex-column justify-content-center"
            style={{
              background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 100%)'
            }}
          >
            <div className="container">
              {/* Breadcrumbs */}
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-3">
                  <li className="breadcrumb-item">
                    <a onClick={()=>navigate('/')} className="text-white text-decoration-none" style={{cursor:'pointer'}}>Home</a>
                  </li>
                  <span className='mx-2' style={{color:'#fff'}}>/</span>
                    <a onClick={()=>navigate('/projects')} style={{cursor:'pointer'}} className="text-white text-decoration-none">Projects</a>
                  
                  <span className='mx-2' style={{color:'#fff'}}>/</span>
                  <span className='text-white' style={{cursor:'pointer'}} >
                    {project.title}
                  </span>
                </ol>
              </nav>

              {/* Project Title */}
              <h1 className="text-white fw-bold display-4 mb-2">{project.title}</h1>
              {/* <p className="text-white-50 lead">{project.short_description}</p> */}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container py-5">
          <div className="row">
            {/* Left Column - Main Content */}
            <div className="col-lg-8 col-12 mb-4">
              <div className="card shadow-sm h-100">
                <div className="card-body p-4">
                  {/* Category Badge - Hidden on large screens, dropdown on small screens */}
                  <div className="d-block d-lg-none mb-3">
                    <div className="dropdown">
                      <button 
                        className="btn btn-outline-primary dropdown-toggle w-100 d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <span>Category: {project.category_name || 'Web Development'}</span>
                      </button>
                      <ul className="dropdown-menu w-100">
                        <li><span className="dropdown-item-text fw-bold">{project.category_name || 'Web Development'}</span></li>
                      </ul>
                    </div>
                  </div>

                  {/* Category Badge - Visible on large screens */}
                  <div className="d-none d-lg-block">
                    <span className="badge bg-primary bg-opacity-10 text-primary fs-6 px-3 py-2 mb-3">
                      {project.category_name || 'Web Development'}
                    </span>
                  </div>

                  {/* Title and Tagline */}
                  <div className="mb-4">
                    <h2 className="fw-bold text-dark mb-2">{project.title}</h2>
                    <p className="text-muted lead border-bottom pb-3 mb-0">{project.short_description}</p>
                  </div>

                  {/* Action Buttons */}
                  <div className="row g-3 mb-4">
                    {project.project_url && (
                      <div className="col-sm-6">
                        <a 
                          href={project.project_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary w-100"
                        >
                          <FontAwesomeIcon icon={faGlobe} className="me-2" />
                          Live Demo
                        </a>
                      </div>
                    )}
                    <div className="col-sm-6">
                      <button className="btn btn-primary w-100">
                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                        View Case Study
                      </button>
                    </div>
                  </div>

                  {/* Tabs */}
                  <ul className="nav nav-tabs nav-justified mb-4" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                        type="button"
                        role="tab"
                      >
                        Overview
                      </button>
                    </li>
                 
                  </ul>

                  {/* Tab Content */}
                  <div className="tab-content">
                    {activeTab === 'overview' && (
                      <div className="tab-pane fade show active">
                        <h4 className="fw-bold text-dark mb-3">Project Description</h4>
                        <p 
                          className="text-muted lh-lg mb-4"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {project.description}
                        </p>


                        {/* Technologies Section */}
                        {technologiesArray.length > 0 && (
                          <>
                            <h4 className="fw-bold text-dark mb-3">Technologies Used</h4>
                            <div className="d-flex flex-wrap gap-2 mb-4">
                              {technologiesArray.map((tech, index) => (
                                <span key={index} className="badge bg-light text-dark border px-3 py-2">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Gallery and Info */}
            <div className="col-lg-4 col-12">
              <div className="d-flex flex-column gap-4">
                {/* Gallery Card */}
                <div className="card shadow-sm border-0">
                  <div className="card-body p-3">
                    <div className="position-relative bg-light rounded overflow-hidden mb-3">
                      {mainImage ? (
                        <img 
                          src={`${API_URL}/${mainImage}`} 
                          alt={project.title}
                          className="w-100"
                          style={{ height: '200px', objectFit: 'fill' }}
                          onError={(e) => {
                            e.target.src = '/images/placeholder-project.png'
                          }}
                        />
                      ) : (
                        <div 
                          className="d-flex align-items-center justify-content-center text-muted"
                          style={{ height: '250px' }}
                        >
                          <span>No Image Available</span>
                        </div>
                      )}
                      
                      {/* Navigation Arrows */}
                      {hasGallery && images.length > 1 && (
                        <>
                          <button 
                            className="position-absolute top-50 start-0 translate-middle-y btn btn-light rounded-circle shadow-sm mx-2"
                            onClick={prevImage}
                            style={{ width: '40px', height: '40px' }}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <button 
                            className="position-absolute top-50 end-0 translate-middle-y btn btn-light rounded-circle shadow-sm mx-2"
                            onClick={nextImage}
                            style={{ width: '40px', height: '40px' }}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Thumbnail Gallery */}
                    {hasGallery && images.length > 1 && (
                      <div className="d-flex gap-2 overflow-auto pb-2">
                        {images.map((image, index) => (
                          <button
                            key={index}
                            className={`btn p-0 border rounded ${index === currentImageIndex ? 'border-primary' : 'border-light'}`}
                            onClick={() => goToImage(index)}
                            style={{ flex: '0 0 auto', width: '60px', height: '60px' }}
                          >
                            <img 
                              src={`${API_URL}/${image}`} 
                              alt={`View ${index + 1}`}
                              className="w-100 h-100 object-fit-cover rounded"
                              onError={(e) => {
                                e.target.src = '/images/placeholder-project.png'
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Project Info Card */}
                <div className="card shadow-sm border-0 sticky-top" style={{ top: '20px' }}>
                  <div className="card-body">
                    <h5 className="fw-bold text-dark mb-3">Project Details</h5>
                    
                    <div className="mb-3">
                      {project.category_name && (
                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon icon={faCode} className="text-primary me-2" />
                          <small className="text-muted">Category: {project.category_name}</small>
                        </div>
                      )}
                      
                      {project.status && (
                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-primary me-2" />
                          <small className="text-muted">Status: {project.status}</small>
                        </div>
                      )}
                      
                      {project.start_date && (
                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon icon={faCalendar} className="text-primary me-2" />
                          <small className="text-muted">
                            Start: {new Date(project.start_date).toLocaleDateString()}
                          </small>
                        </div>
                      )}
                      
                      {project.end_date && (
                        <div className="d-flex align-items-center mb-2">
                          <FontAwesomeIcon icon={faCalendar} className="text-primary me-2" />
                          <small className="text-muted">
                            End: {new Date(project.end_date).toLocaleDateString()}
                          </small>
                        </div>
                      )}
                    </div>

                    <ul className="list-unstyled mb-3">
                      <li className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                        <small className="text-muted">Complete Project Documentation</small>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                        <small className="text-muted">Source Code Access</small>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                        <small className="text-muted">Technical Support</small>
                      </li>
                      <li className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                        <small className="text-muted">Deployment Assistance</small>
                      </li>
                    </ul>

                    <div className="border-top pt-3">
                      <h6 className="fw-bold text-dark mb-2">Quick Links</h6>
                      <div className="d-flex flex-column gap-1">
                        {project.project_url && (
                          <a href={project.project_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-primary">
                            <FontAwesomeIcon icon={faGlobe} className="me-2" />
                            <small>Live Demo</small>
                          </a>
                        )}
                        {project.github_url && (
                          <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-primary">
                            <FontAwesomeIcon icon={faGithub} className="me-2" />
                            <small>View on GitHub</small>
                          </a>
                        )}
                        <a href="#contact" className="text-decoration-none text-primary">
                          <FontAwesomeIcon icon={faCodeBranch} className="me-2" />
                          <small>Similar Projects</small>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  )
}